// web-v2/src/services/docs-open.service.js
//
// Opening a client document through the logged door (personal-logins final-fix COMPLETE-1, PLAN
// section 6, DESIGN-REVIEW M15, QUESTIONS L4-Q3). A WorkDrive Permalink rendered as a plain link
// never reaches the API, so a document open was not logged with the person. Every V2 surface that
// opens a stored Permalink calls openDocument instead: it opens a blank tab synchronously (inside
// the click, or the popup blocker eats it), asks GET /api/tracon/docs/open/:fileId (broker gate,
// logged download) for the url, and points the tab there.
// Notify, don't gate: when the door cannot answer (an API older than the door, a network blip) the
// document still opens from its own Permalink and the broker is told the open was not logged.
// STATED BLIND SPOT: a link that is not a WorkDrive FILE Permalink (a folder link, another host)
// opens directly, unlogged and silently, as L4-Q3 ruled for folder links.
// Relative imports, so tests/docs-open-client.test.js can import it under node.

import { api } from '../lib/api.js';

export const WORKDRIVE_FILE_RX = /^https:\/\/workdrive\.zoho\.com\/file\/([A-Za-z0-9]{20,64})(?:[/?#]|$)/;
export const docsOpenPath = (fileId) => `/api/tracon/docs/open/${encodeURIComponent(fileId)}`;
export const DOC_NOT_LOGGED_WORDS = 'Opened from its WorkDrive link: this open was not logged.';
export const POPUP_BLOCKED_WORDS = 'The browser blocked the new tab. Allow pop-ups for TRACON to open documents.';
const DOOR_MISSING = Object.freeze({ status: 404, code: 'Not found' });

/** The WorkDrive resource id of a file Permalink, or null for anything else. */
export function workdriveFileId(permalink) {
  const m = WORKDRIVE_FILE_RX.exec(String(permalink || ''));
  return m ? m[1] : null;
}

const defaultGet = (p) => api.get(p, { expected: [DOOR_MISSING] });

/**
 * Open one stored document link. Resolves to { opened, via } where via is 'door' (logged),
 * 'direct' (not a file Permalink, the blind spot) or 'fallback' (the door did not answer).
 */
export async function openDocument(permalink, { win = globalThis.window, get = defaultGet, notify = () => {} } = {}) {
  const link = String(permalink || '');
  if (!/^https?:\/\//i.test(link)) return { opened: false, via: null };
  const tab = win && typeof win.open === 'function' ? win.open('', '_blank') : null;
  if (!tab) { notify(POPUP_BLOCKED_WORDS); return { opened: false, via: null }; }
  try { tab.opener = null; } catch { /* a cross-origin tab already */ }
  const id = workdriveFileId(link);
  let url = link; let via = 'direct';
  if (id) {
    via = 'fallback';
    try {
      const d = await get(docsOpenPath(id));
      if (d && d.success === true && typeof d.url === 'string' && workdriveFileId(d.url) === id) { url = d.url; via = 'door'; }
    } catch { /* the fallback below says so */ }
    if (via === 'fallback') notify(DOC_NOT_LOGGED_WORDS);
  }
  tab.location.href = url;
  return { opened: true, via };
}

// THE STORED DOCUMENT (deals-door-1 round 3 R2d-Q1; paperwork-1 D8, L1b-B3): any document in TRACON's documents home opens
// through GET /api/tracon/docs/stored/:documentId (broker gate, logged download). The bytes come back with the broker's
// token, so the tab is opened blank inside the click and pointed at an object URL once they arrive.
// THE BLOB'S TYPE IS SNIFFED FROM THE BYTES, from an allowlist of three (PDF, PNG, JPEG), NEVER the server's Content-Type:
// a file that is none of them is typed application/octet-stream and SAVED under the name the door's Content-Disposition
// gives, never rendered in the tab. A refusal (bytes that no longer match their fingerprint, a store that did not answer)
// closes the tab and says so.
export const storedDocPath = (documentId) => `/api/tracon/docs/stored/${encodeURIComponent(documentId)}`;
export const STORED_NOT_OPENED_WORDS = 'The document could not be opened.';
export const STORED_INLINE_TYPES = Object.freeze(['application/pdf', 'image/png', 'image/jpeg']);
const SAVED_TYPE = 'application/octet-stream';

const startsWith = (bytes, magic) => bytes.length >= magic.length && magic.every((b, i) => bytes[i] === b);
/** The type the bytes ARE, from the allowlist, else application/octet-stream. */
export function sniffStoredType(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (startsWith(b, [0x25, 0x50, 0x44, 0x46, 0x2d])) return 'application/pdf';
  if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (startsWith(b, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  return SAVED_TYPE;
}

/** The filename a Content-Disposition names (filename* first, RFC 5987), or null. */
export function filenameFromDisposition(header) {
  const h = String(header || '');
  const star = /filename\*=UTF-8''([^;]+)/i.exec(h);
  if (star) { try { return decodeURIComponent(star[1].trim()); } catch { /* the plain one below */ } }
  const plain = /filename="([^"]*)"/i.exec(h);
  return plain ? plain[1] : null;
}

function saveBlob(url, name, doc) {
  const a = doc.createElement('a');
  a.href = url; a.download = name || 'document'; a.rel = 'noopener';
  doc.body.appendChild(a); a.click(); a.remove();
}

export async function openStoredDocument(documentId, { win = globalThis.window, fetchImpl = globalThis.fetch, notify = () => {}, token = null, urlApi = globalThis.URL, doc = globalThis.document } = {}) {
  const tab = win && typeof win.open === 'function' ? win.open('', '_blank') : null;
  if (!tab) { notify(POPUP_BLOCKED_WORDS); return { opened: false }; }
  try { tab.opener = null; } catch { /* a cross-origin tab already */ }
  try {
    const bearer = token || (globalThis.localStorage ? globalThis.localStorage.getItem('tracon_token') : null);
    const res = await fetchImpl(storedDocPath(documentId), { headers: bearer ? { Authorization: `Bearer ${bearer}` } : {} });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    const buf = await res.arrayBuffer();
    const type = sniffStoredType(new Uint8Array(buf));
    const url = urlApi.createObjectURL(new Blob([buf], { type }));
    if (STORED_INLINE_TYPES.includes(type)) { tab.location.href = url; return { opened: true, type }; }
    try { tab.close(); } catch { /* already gone */ }
    saveBlob(url, filenameFromDisposition(res.headers && res.headers.get('content-disposition')), doc);
    setTimeout(() => { try { urlApi.revokeObjectURL(url); } catch { /* gone */ } }, 60000);
    return { opened: false, saved: true, type };
  } catch (e) {
    try { tab.close(); } catch { /* already gone */ }
    notify(`${STORED_NOT_OPENED_WORDS} (${e.message})`);
    return { opened: false };
  }
}

// A document known by its WorkDrive file id alone (a ledger row's carrier document holds the id, not a Permalink): the same
// logged door, the same blank-tab-inside-the-click rule. When the door does not answer the tab closes and the broker is told.
export const DOC_NOT_OPENED_WORDS = 'The document could not be opened.';
export async function openWorkdriveFile(fileId, { win = globalThis.window, get = defaultGet, notify = () => {} } = {}) {
  const tab = win && typeof win.open === 'function' ? win.open('', '_blank') : null;
  if (!tab) { notify(POPUP_BLOCKED_WORDS); return { opened: false }; }
  try { tab.opener = null; } catch { /* a cross-origin tab already */ }
  try {
    const d = await get(docsOpenPath(fileId));
    if (!(d && d.success === true && workdriveFileId(d.url) === String(fileId))) throw new Error('the open door did not answer');
    tab.location.href = d.url;
    return { opened: true };
  } catch (e) {
    try { tab.close(); } catch { /* already gone */ }
    notify(`${DOC_NOT_OPENED_WORDS} (${e.message})`);
    return { opened: false };
  }
}
